import { createAction } from "typesafe-actions";
import { Dispatch } from "redux";
import { Task } from "../interfaces/Task";
import { TaskModel } from "../interfaces/Models/TaskModel";
import * as ClientTask from "../client/task";

// Action types
const GET_TASKS_REQUEST = "task/GET_TASKS_REQUEST";
const GET_TASKS_SUCCESS = "task/GET_TASKS_SUCCESS";
const GET_TASKS_FAILURE = "task/GET_TASKS_FAILURE";

const GET_MY_TASKS_REQUEST = "task/GET_MY_TASKS_REQUEST";
const GET_MY_TASKS_SUCCESS = "task/GET_MY_TASKS_SUCCESS";
const GET_MY_TASKS_FAILURE = "task/GET_MY_TASKS_FAILURE";

const CREATE_TASK_REQUEST = "task/CREATE_TASK_REQUEST";
const CREATE_TASK_SUCCESS = "task/CREATE_TASK_SUCCESS";
const CREATE_TASK_FAILURE = "task/CREATE_TASK_FAILURE";

const UPDATE_TASK_STATUS_REQUEST = "task/UPDATE_TASK_STATUS_REQUEST";
const UPDATE_TASK_STATUS_SUCCESS = "task/UPDATE_TASK_STATUS_SUCCESS";
const UPDATE_TASK_STATUS_FAILURE = "task/UPDATE_TASK_STATUS_FAILURE";

const DELETE_TASK_REQUEST = "task/DELETE_TASK_REQUEST";
const DELETE_TASK_SUCCESS = "task/DELETE_TASK_SUCCESS";
const DELETE_TASK_FAILURE = "task/DELETE_TASK_FAILURE";

// Action creators
const getTasksRequest = createAction(GET_TASKS_REQUEST)();
const getTasksSuccess = createAction(GET_TASKS_SUCCESS)<TaskModel>();
const getTasksFailure = createAction(GET_TASKS_FAILURE)();

const getMyTasksRequest = createAction(GET_MY_TASKS_REQUEST)();
const getMyTasksSuccess = createAction(GET_MY_TASKS_SUCCESS)<Task[]>();
const getMyTasksFailure = createAction(GET_MY_TASKS_FAILURE)();

const createTaskRequest = createAction(CREATE_TASK_REQUEST)();
const createTaskSuccess = createAction(CREATE_TASK_SUCCESS)<Task>();
const createTaskFailure = createAction(CREATE_TASK_FAILURE)();

const updateTaskStatusRequest = createAction(UPDATE_TASK_STATUS_REQUEST)();
const updateTaskStatusSuccess = createAction(UPDATE_TASK_STATUS_SUCCESS)<Task>();
const updateTaskStatusFailure = createAction(UPDATE_TASK_STATUS_FAILURE)();

const deleteTaskRequest = createAction(DELETE_TASK_REQUEST)();
const deleteTaskSuccess = createAction(DELETE_TASK_SUCCESS)<number>();
const deleteTaskFailure = createAction(DELETE_TASK_FAILURE)();

// Thunk actions
export const getTasks = (orderBy: boolean, orderColumn: string, toTake: number, toSkip: number, projectId?: number, status?: number) => (dispatch: Dispatch) => {
    dispatch(getTasksRequest());
    return ClientTask.getAllTasks(orderBy, orderColumn, toTake, toSkip, projectId, status)
        .then(response => {
            dispatch(getTasksSuccess(response.data));
            return response.data;
        })
        .catch(() => {
            dispatch(getTasksFailure());
        });
};

export const getMyTasks = (projectId?: number) => (dispatch: Dispatch) => {
    dispatch(getMyTasksRequest());
    return ClientTask.getMyTasks(projectId)
        .then(response => {
            dispatch(getMyTasksSuccess(response.data));
            return response.data;
        })
        .catch(() => {
            dispatch(getMyTasksFailure());
        });
};

export const createTask = (task: any) => (dispatch: Dispatch) => {
    dispatch(createTaskRequest());
    return ClientTask.createTask(task)
        .then(response => {
            dispatch(createTaskSuccess(response.data));
            return response.data;
        })
        .catch(() => {
            dispatch(createTaskFailure());
        });
};

export const createTaskWithImages = (task: any, images: File[]) => (dispatch: Dispatch) => {
    dispatch(createTaskRequest());
    return ClientTask.createTaskWithImages(task, images)
        .then(response => {
            dispatch(createTaskSuccess(response.data));
            return response.data;
        })
        .catch(() => {
            dispatch(createTaskFailure());
        });
};

export const updateTaskStatus = (taskId: number, model: any) => (dispatch: Dispatch) => {
    dispatch(updateTaskStatusRequest());
    return ClientTask.updateTaskStatus(taskId, model)
        .then(response => {
            dispatch(updateTaskStatusSuccess(response.data));
            return response.data;
        })
        .catch(() => {
            dispatch(updateTaskStatusFailure());
        });
};

export const deleteTask = (taskId: number) => (dispatch: Dispatch) => {
    dispatch(deleteTaskRequest());
    return ClientTask.deleteTask(taskId)
        .then(() => {
            dispatch(deleteTaskSuccess(taskId));
            return taskId;
        })
        .catch(() => {
            dispatch(deleteTaskFailure());
        });
};

// Initial state
export interface TaskState {
    tasks: Task[];
    myTasks: Task[];
    totalCount: number;
    isLoading: boolean;
}

const initialState: TaskState = {
    tasks: [],
    myTasks: [],
    totalCount: 0,
    isLoading: false
};

// Reducer
export default function taskReducer(state = initialState, action: any): TaskState {
    switch (action.type) {
        case GET_TASKS_REQUEST:
        case GET_MY_TASKS_REQUEST:
        case CREATE_TASK_REQUEST:
        case UPDATE_TASK_STATUS_REQUEST:
        case DELETE_TASK_REQUEST:
            return {
                ...state,
                isLoading: true
            };

        case GET_TASKS_SUCCESS:
            return {
                ...state,
                tasks: action.payload.tasks,
                totalCount: action.payload.totalCount,
                isLoading: false
            };

        case GET_MY_TASKS_SUCCESS:
            return {
                ...state,
                myTasks: action.payload,
                isLoading: false
            };

        case CREATE_TASK_SUCCESS:
            return {
                ...state,
                tasks: [...state.tasks, action.payload],
                isLoading: false
            };

        case UPDATE_TASK_STATUS_SUCCESS:
            return {
                ...state,
                tasks: state.tasks.map(task => 
                    task.id === action.payload.id ? action.payload : task
                ),
                myTasks: state.myTasks.map(task => 
                    task.id === action.payload.id ? action.payload : task
                ),
                isLoading: false
            };

        case DELETE_TASK_SUCCESS:
            return {
                ...state,
                tasks: state.tasks.filter(task => task.id !== action.payload),
                myTasks: state.myTasks.filter(task => task.id !== action.payload),
                isLoading: false
            };

        case GET_TASKS_FAILURE:
        case GET_MY_TASKS_FAILURE:
        case CREATE_TASK_FAILURE:
        case UPDATE_TASK_STATUS_FAILURE:
        case DELETE_TASK_FAILURE:
            return {
                ...state,
                isLoading: false
            };

        default:
            return state;
    }
}
